/**
 * DocSidebar - Collapsible tree navigation matching PDF Contents.
 * Sections expand/collapse; leaf items are clickable links.
 * Supports multiple doc apps via toc and backLabel props.
 */
import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { TOC } from "../data/toc";

// Check if any descendant has the current path
function hasDescendantWithPath(item, basePath, currentPath) {
  if (item.path) return currentPath === `${basePath}/${item.path}`;
  if (!item.children) return false;
  return item.children.some((c) => hasDescendantWithPath(c, basePath, currentPath));
}

function TocItem({ item, basePath, depth = 0, expandedSections, onToggle, currentPath }) {
  const hasChildren = item.children && item.children.length > 0;
  const isLeaf = !!item.path;

  if (isLeaf) {
    const to = `${basePath}/${item.path}`;
    return (
      <NavLink
        to={to}
        className={({ isActive }) =>
          `block py-1.5 px-3 rounded-md text-sm transition-colors pl-${Math.min(depth * 4 + 12, 24)} ${
            isActive
              ? "bg-sepm-primary-light dark:bg-gray-700 text-sepm-primary dark:text-sepm-primary-dark font-medium"
              : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100"
          }`
        }
        style={{ paddingLeft: `${depth * 12 + 12}px` }}
      >
        {item.label}
      </NavLink>
    );
  }

  // Section with children
  const sectionKey = item.label;
  const isExpanded = expandedSections.has(sectionKey);
  const hasActiveChild = hasChildren && item.children.some((c) => hasDescendantWithPath(c, basePath, currentPath));

  return (
    <div key={sectionKey} className="mb-1">
      <button
        type="button"
        onClick={() => onToggle(sectionKey)}
        className={`w-full flex items-center gap-1 py-1.5 px-3 rounded-md text-sm text-left transition-colors ${
          hasActiveChild ? "text-sepm-primary dark:text-sepm-primary-dark font-medium" : "text-gray-700 dark:text-gray-300"
        } hover:bg-gray-100 dark:hover:bg-gray-700`}
        style={{ paddingLeft: `${depth * 12 + 12}px` }}
      >
        <span
          className={`inline-block w-4 h-4 flex-shrink-0 transition-transform ${isExpanded ? "rotate-90" : ""}`}
          aria-hidden
        >
          ▶
        </span>
        {item.label}
      </button>
      {isExpanded && hasChildren && (
        <div className="mt-0.5">
          {item.children.map((child) => (
            <TocItem
              key={child.label + (child.path || "")}
              item={child}
              basePath={basePath}
              depth={depth + 1}
              expandedSections={expandedSections}
              onToggle={onToggle}
              currentPath={currentPath}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function DocSidebar({ basePath = "/docs/x-translator-sw", toc = TOC, backLabel = "← X-Translator SW" }) {
  const location = useLocation();
  const currentPath = location.pathname;

  // Track expanded sections - expand sections containing current page
  const getInitialExpanded = () => {
    const initial = new Set();
    const currentSlug = currentPath.replace(basePath, "").replace(/^\//, "");
    const findAndExpand = (items, parents = []) => {
      for (const item of items) {
        if (item.path === currentSlug) {
          parents.forEach((p) => initial.add(p));
          return true;
        }
        if (item.children && findAndExpand(item.children, [...parents, item.label])) {
          initial.add(item.label);
          return true;
        }
      }
      return false;
    };
    findAndExpand(toc);
    if (initial.size === 0) {
      toc.forEach((t) => t.children && initial.add(t.label));
    }
    return initial;
  };
  const [expandedSections, setExpandedSections] = useState(getInitialExpanded);

  // Expand section containing current page when route changes
  useEffect(() => {
    const currentSlug = currentPath.replace(basePath + "/", "").replace(basePath, "");
    const toExpand = new Set();
    const findAndExpand = (items, parents = []) => {
      for (const item of items) {
        if (item.path === currentSlug) {
          parents.forEach((p) => toExpand.add(p));
          return true;
        }
        if (item.children) {
          if (findAndExpand(item.children, [...parents, item.label])) {
            toExpand.add(item.label);
            return true;
          }
        }
      }
      return false;
    };
    findAndExpand(toc);
    setExpandedSections((prev) => {
      const next = new Set(prev);
      toExpand.forEach((s) => next.add(s));
      return next;
    });
  }, [currentPath, basePath, toc]);

  const onToggle = (sectionKey) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionKey)) {
        next.delete(sectionKey);
      } else {
        next.add(sectionKey);
      }
      return next;
    });
  };

  return (
    <nav className="space-y-1" aria-label="Documentation navigation">
      <Link
        to={basePath}
        className="block py-2 px-3 rounded-md text-sm font-medium text-sepm-primary dark:text-sepm-primary-dark hover:bg-sepm-primary-light dark:hover:bg-gray-700 mb-4"
      >
        {backLabel}
      </Link>

      <div className="space-y-0.5">
        {toc.map((item) => (
          <TocItem
            key={item.label + (item.path || "")}
            item={item}
            basePath={basePath}
            expandedSections={expandedSections}
            onToggle={onToggle}
            currentPath={currentPath}
          />
        ))}
      </div>
    </nav>
  );
}
